import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import type { ForgeRemoteRuntime } from "./api/forgeRemoteApi";
import { PortalApiError } from "./api/portalApi";
import { AppShell } from "./components/AppShell";
import { RemoteDialogs } from "./components/RemoteDialogs";
import { BrandMark } from "./components/Icon";
import type { Capability, DialogState } from "./domain";
import { ActivationScreen } from "./screens/ActivationScreen";
import { AttentionScreen } from "./screens/AttentionScreen";
import { BoardScreen } from "./screens/BoardScreen";
import { CrewPulseScreen } from "./screens/CrewPulseScreen";
import { CrewsScreen } from "./screens/CrewsScreen";
import { ManagerScreen } from "./screens/ManagerScreen";
import { SessionScreen } from "./screens/SessionScreen";
import { RemoteProvider } from "./state/RemoteContext";
import { useActivationState } from "./state/useActivationState";
import { useProjection, useRemoteActions } from "./state/remoteQueries";

export function App({ runtime, freshActivationLink }: { runtime: ForgeRemoteRuntime; freshActivationLink: boolean }) {
  const activation = useActivationState(runtime.api, runtime.sourceKind, freshActivationLink);
  const projection = useProjection(runtime.api, activation.activated);
  const actions = useRemoteActions(runtime.api);
  const [dialog, setDialog] = useState<DialogState>(null);

  useEffect(() => {
    const error = projection.error;
    if (projection.sessionTerminal
        || (error instanceof PortalApiError && [401, 403].includes(error.status ?? 0))) {
      activation.forget();
    }
  }, [activation.forget, projection.error, projection.sessionTerminal]);

  useEffect(() => {
    if (!activation.activated || !projection.data) return;
    const expiresAt = Date.parse(projection.data.session.expiresAt);
    if (!Number.isFinite(expiresAt)) return;
    const remaining = expiresAt - Date.now();
    if (remaining <= 0) {
      activation.forget();
      return;
    }
    const timer = window.setTimeout(activation.forget, Math.min(remaining, 2_147_483_647));
    return () => window.clearTimeout(timer);
  }, [activation.activated, activation.forget, projection.data]);

  if (!activation.activated) return <ActivationScreen api={runtime.api} onActivated={activation.activate} />;
  if (projection.isPending) return <main className="app-state"><BrandMark className="brand-mark loading-mark" /><h1>Restoring Forge Remote</h1><p role="status">Reading the latest trusted projection…</p></main>;
  if (projection.isError || !projection.data) {
    return <main className="app-state"><h1>Projection unavailable</h1><p>The client could not restore the scoped Forge projection.</p><button type="button" className="primary-button" onClick={() => projection.refetch()}>Try again</button><button type="button" className="text-button" onClick={activation.forget}>Use a new activation</button></main>;
  }

  const fixture = projection.transportState === "connected"
    ? projection.data
    : {
        ...projection.data,
        session: {
          ...projection.data.session,
          gateway: {
            ...projection.data.session.gateway,
            state: projection.transportState,
          },
          projection: {
            ...projection.data.session.projection,
            state: projection.transportState === "offline" ? "stale" as const : "delayed" as const,
          },
        },
      };
  const can = (capability: Capability) => fixture.session.capabilities.includes(capability);
  const transportReady = fixture.session.gateway.state === "connected" && fixture.session.host.state === "reachable" && fixture.session.projection.state === "live";
  const authorityReady = fixture.session.commandAuthority.state === "available";
  const commandProtectionReady = runtime.api.hasCommandProtection();
  const canMutate = transportReady && authorityReady && commandProtectionReady;
  const mutationBlockReason = !authorityReady
    ? fixture.session.commandAuthority.reason
    : !commandProtectionReady
      ? "Session command protection is unavailable; redeem a new activation."
    : !transportReady
      ? "Commands require a connected gateway, reachable Forge host and live projection."
      : null;
  const chatTransportReady = fixture.session.gateway.state === "connected" && fixture.session.host.state === "reachable";
  const canChat = can("manager.chat") && chatTransportReady && authorityReady && commandProtectionReady;
  const managerBlockReason = !can("manager.chat")
    ? "Manager chat is not granted to this device."
    : !authorityReady
      ? fixture.session.commandAuthority.reason
      : !commandProtectionReady
        ? "Session command protection is unavailable; redeem a new activation."
      : !chatTransportReady
        ? "Manager chat requires a connected gateway and reachable Forge host."
        : null;
  const context = { fixture, actions, dialog, setDialog, can, canMutate, mutationBlockReason, canChat, managerBlockReason, historyReset: projection.historyReset, sourceKind: runtime.sourceKind };

  return (
    <RemoteProvider value={context}>
      <Routes>
        <Route element={<AppShell />}>
          <Route path="/attention" element={<AttentionScreen />} />
          <Route path="/crews" element={<CrewsScreen />} />
          <Route path="/session" element={<SessionScreen onEnd={activation.end} />} />
          <Route path="/crew/:crewId/pulse" element={<CrewPulseScreen />} />
          <Route path="/crew/:crewId/manager" element={<ManagerScreen />} />
          <Route path="/crew/:crewId/board" element={<BoardScreen />} />
          <Route path="*" element={<Navigate to="/attention" replace />} />
        </Route>
      </Routes>
      <RemoteDialogs />
    </RemoteProvider>
  );
}
